import { dispatchManualSpatialCommand } from './3d/spatialCommands';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import { flushMediaDrafts, hasMediaDraft } from './mediaSaveGuard';

// Other existing owners (notably explicit account logout) request this guard;
// the event carries no draft, history or persistence authority.
export function requestWorkspaceExit() {
  const detail = { promise: null };
  window.dispatchEvent(new CustomEvent('doar:workspace:before-exit', { detail }));
  return detail.promise || Promise.resolve(true);
}
export function exportWorkspaceDraft(controller) {
  const state = controller.getSnapshot();
  const url = URL.createObjectURL(new Blob([JSON.stringify({ target: state.target, draftEpoch: state.draftEpoch, document: state.recoveryDocument || state.document }, null, 2)], { type: 'application/json' }));
  const link = document.createElement('a'); link.href = url; link.download = 'doar-local-draft.json'; link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const identity = state => JSON.stringify([state.target?.projectId, state.target?.doarId, state.target?.assetId]);
const protectedDraft = state => state.document && (state.dirty || state.busy || state.conflict);

// Validate the raw attribute before URL normalization can hide controls or
// backslashes. Script/data documents never become navigation destinations.
export function workspaceLinkDestination(href, base, download = false) {
  if (typeof href !== 'string') return null;
  const unsafe = value => value.startsWith('//') || Array.from(value).some(character => character === '\\' || character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);
  let decoded = href;
  try {
    for (let depth = 0; depth < 4; depth++) {
      if (unsafe(decoded)) return null;
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    }
    if (unsafe(decoded)) return null;
    const parsed = new URL(href, base), decodedUrl = new URL(decoded, base);
    if (parsed.username || parsed.password || !['http:', 'https:', 'mailto:', 'tel:', 'blob:', 'data:'].includes(decodedUrl.protocol)) return null;
    if (['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol)) return parsed;
    if (download && parsed.protocol === 'blob:' && parsed.origin === new URL(base).origin) return parsed;
    if (download && /^data:image\/(?:png|jpeg|webp|gif);base64,[a-z0-9+/=]+$/i.test(href)) return parsed;
  } catch { return null; }
  return null;
}
let activePopGuard = null, installed = false;
// Window is the POP event target: capture does not reorder listeners there.
// The existing entrypoint installs this inert broker before BrowserRouter creates
// its history listener. Only the mounted session owns the active callback.
export function installWorkspacePopGuard() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('popstate', event => activePopGuard?.(event), true);
}

export function registerWorkspacePopGuard(callback) {
  if (typeof callback !== 'function') throw new TypeError('A navigation callback is required.');
  installWorkspacePopGuard();
  activePopGuard = callback;
  return () => { if (activePopGuard === callback) activePopGuard = null; };
}

export default function useWorkspaceDraftGuard(controller, accountId) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const latest = useRef(controller); latest.current = controller;
  const revisionReceipt = useRef(null);
  const withRevisionReceipt = (receipt, apply) => {
    const state=latest.current.getSnapshot();
    if(state.dirty||state.conflict)return false;
    revisionReceipt.current={...receipt,href:window.location.href,identity:identity(state),epoch:state.draftEpoch};
    try{return apply();}finally{revisionReceipt.current=null;}
  };
  const pending = useRef(null), [dialog, setDialog] = useState(null), [message, setMessage] = useState('');
  const finish = useCallback(allowed => { const request = pending.current; pending.current = null; setDialog(null); setMessage(''); request?.resolve(allowed); }, []);
  const confirm = useCallback(async () => {
    if (hasMediaDraft() && !await flushMediaDrafts()) return false;
    const state = latest.current.getSnapshot();
    if (!protectedDraft(state)) return Promise.resolve(true);
    if (pending.current) return Promise.resolve(false);
    return new Promise(resolve => { const request = { resolve, identity: identity(state), epoch: state.draftEpoch }; pending.current = request; setDialog(request); });
  }, []);
  const decide = async choice => {
    if (choice === 'cancel') { finish(false); return; }
    const request = pending.current, state = latest.current.getSnapshot();
    if (!request) return;
    if (identity(state) !== request.identity || state.draftEpoch !== request.epoch) { setMessage('The draft changed while this choice was open. Cancel and review the current scene.'); return; }
    if (state.busy) { setMessage('Wait for the current load or save to finish, or cancel.'); return; }
    if (choice === 'discard') {
      if (!window.confirm('Discard this local draft and its unsaved changes? Export a copy first if you need it.')) return;
      if (latest.current.discard()) finish(true);
      return;
    }
    if (state.conflict) { setMessage('This scene has a revision conflict. Export the local draft before explicitly discarding or reloading it.'); return; }
    const outcome = await dispatchManualSpatialCommand(latest.current, 'document.save');
    const saved = outcome.saved;
    if (pending.current !== request) return;
    const next = latest.current.getSnapshot();
    if (!saved || identity(next) !== request.identity || next.dirty || next.busy || next.conflict) { setMessage(next.error || 'Save did not complete. Navigation was cancelled; your local draft is intact.'); return; }
    finish(true);
  };
  useEffect(() => {
    const beforeExit = event => {
      const previous = event.detail.promise;
      event.detail.promise = previous ? Promise.resolve(previous).then(ok => ok && confirm()) : confirm();
    };
    window.addEventListener('doar:workspace:before-exit', beforeExit);
    return () => window.removeEventListener('doar:workspace:before-exit', beforeExit);
  }, [confirm]);
  useEffect(() => { setDialog(null); return () => { pending.current?.resolve(false); pending.current = null; }; }, [accountId]);
  useEffect(() => {
    const request = pending.current;
    if (request && request.identity !== identity(controller.getSnapshot())) finish(false);
  }, [controller.target?.doarId, controller.target?.assetId, controller.target?.projectId, controller, finish]);
  useEffect(() => {
    // Wrap the installed Router navigator, never window.history or another router.
    // Save/capture's own revision replacement is a non-destructive same-target update.
    let anchorIndex = window.history.state?.idx;
    const original = { push: navigator.push, replace: navigator.replace };
    const destinationFor = to => workspaceLinkDestination(typeof to === 'string' ? to : `${to.pathname || window.location.pathname}${to.search || ''}${to.hash || ''}`, window.location.href);
    const safeReplace = to => {
      const next = destinationFor(to);
      const current = new URL(window.location.href), state = latest.current.getSnapshot();
      if (!next || next.origin !== current.origin || next.pathname !== current.pathname) return false;
      const keys = new Set([...current.searchParams.keys(), ...next.searchParams.keys()]);
      const receipt=revisionReceipt.current;
      // The spatial controller may bump its epoch before the route catches up.
      // This synchronous receipt grants only its exact parent -> saved URL;
      // it never grants navigation to another project/asset or a dirty scene.
      if(receipt&&receipt.href===current.href&&receipt.identity===identity(state)&&!state.dirty&&!state.conflict&&next.hash===current.hash&&next.searchParams.get('revision')===receipt.revisionId&&(!current.searchParams.get('revision')||current.searchParams.get('revision')===receipt.parentRevisionId)){
        if([...keys].every(key=>current.searchParams.get(key)===next.searchParams.get(key)||key==='revision'||(key==='mediaView'&&['1D','2D','2.5D'].includes(next.searchParams.get(key)))))return true;
      }
      return [...keys].every(key => current.searchParams.get(key) === next.searchParams.get(key) || (key === 'revision' && next.searchParams.get(key) === state.target.revisionId) || (key === 'selectedAsset' && next.searchParams.get(key) === state.target.assetId) || (key === 'insertAsset' && current.searchParams.get(key) === state.target.assetId && !next.searchParams.has(key)));
    };
    const wrap = method => (...args) => {
      const destination = destinationFor(args[0]);
      if (!destination || destination.origin !== window.location.origin || !['http:', 'https:'].includes(destination.protocol)) return;
      const proceed = () => { const result = original[method].apply(navigator, args); anchorIndex = window.history.state?.idx; return result; };
      if ((!protectedDraft(latest.current.getSnapshot()) && !hasMediaDraft()) || (method === 'replace' && safeReplace(args[0]))) return proceed();
      confirm().then(ok => { if (ok) proceed(); });
    };
    for (const method of ['push', 'replace']) navigator[method] = wrap(method);
    const links = event => {
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || event.defaultPrevented) return;
      const url = workspaceLinkDestination(anchor.getAttribute('href'), window.location.href, anchor.hasAttribute('download'));
      if (!url) { event.preventDefault(); event.stopPropagation(); return; }
      if (anchor.hasAttribute('download') || (!protectedDraft(latest.current.getSnapshot()) && !hasMediaDraft())) return;
      const destination = url.href, target = anchor.target;
      if (destination === window.location.href) return;
      event.preventDefault(); event.stopPropagation();
      confirm().then(ok => { if (ok) { if (target === '_blank') window.open(destination, '_blank', 'noopener'); else window.location.assign(destination); } });
    };
    document.addEventListener('click', links, true);
    let pop = null;
    const onPop = event => {
      const next = event.state?.idx;
      if (!Number.isInteger(next) || !Number.isInteger(anchorIndex)) return; // native beforeunload owns foreign document exits
      if (pop?.replay) { pop = null; anchorIndex = next; return; }
      if (pop) {
        event.stopImmediatePropagation();
        if (next !== anchorIndex) { window.history.go(anchorIndex - next); return; }
        if (pop.restoring) {
          pop.restoring = false; const attempt = pop;
          confirm().then(ok => { if (pop !== attempt) return; if (ok) { pop.replay = true; window.history.go(attempt.delta); } else pop = null; });
        }
        return;
      }
      if (!protectedDraft(latest.current.getSnapshot()) && !hasMediaDraft()) { anchorIndex = next; return; }
      event.stopImmediatePropagation(); pop = { delta: next - anchorIndex, restoring: true };
      window.history.go(anchorIndex - next);
    };
    activePopGuard = onPop;
    return () => { navigator.push = original.push; navigator.replace = original.replace; document.removeEventListener('click', links, true); if (activePopGuard === onPop) activePopGuard = null; pop = null; };
  }, [navigator, confirm]);
  return { confirm, dialog, message, decide, controller, withRevisionReceipt };
}

export function WorkspaceDraftDialog({ guard }) {
  const root = useRef(null), invoker = useRef(null);
  useEffect(() => {
    if (!guard?.dialog) return;
    invoker.current = document.activeElement;
    const shell = root.current?.closest('.editor-shell');
    const background = [...(shell?.children || [])].filter(element => !element.contains(root.current));
    const attributes = background.map(element => ({element,inert:element.inert,hidden:element.getAttribute('aria-hidden')}));
    background.forEach(element => { element.inert = true; element.setAttribute('aria-hidden','true'); });
    root.current?.querySelector('[data-cancel]')?.focus();
    return () => {
      attributes.forEach(({element,inert,hidden}) => {element.inert=inert;if(hidden === null)element.removeAttribute('aria-hidden');else element.setAttribute('aria-hidden',hidden);});
      if (invoker.current?.isConnected) invoker.current.focus();
    };
  }, [guard?.dialog]);
  if (!guard?.dialog) return null;
  const state = guard.controller.getSnapshot();
  return <div className="workspace-draft-scrim"><section ref={root} role="dialog" aria-modal="true" aria-label="Unsaved spatial draft" onKeyDown={event => {
    if (event.defaultPrevented || event.target.closest?.('[role="dialog"],dialog') !== root.current) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); guard.decide('cancel'); }
    if (event.key === 'Tab') { const buttons = [...root.current.querySelectorAll('button:not(:disabled)')]; const index = buttons.indexOf(document.activeElement), next = (index + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length; event.preventDefault(); buttons[next]?.focus(); }
  }}><h2>Keep your scene changes?</h2><p>{state.conflict ? state.error || 'This scene has a revision conflict. Your draft is preserved.' : 'Save this spatial draft before leaving, discard it explicitly, or stay here.'}</p>
    {guard.message && <p role="alert">{guard.message}</p>}
    <button type="button" disabled={state.busy || state.conflict} onClick={() => guard.decide('save')}>Save</button>
    <button type="button" disabled={state.busy} onClick={() => exportWorkspaceDraft(guard.controller)}>Export local draft</button>
    <button type="button" disabled={state.busy} onClick={() => guard.decide('discard')}>Discard</button>
    <button type="button" data-cancel onClick={() => guard.decide('cancel')}>Cancel</button>
  </section></div>;
}
